import { computeCartPricing } from '@bonboca/shared-domain';
import {
  cartReducer,
  cartStateSchema,
  isCartExpired,
  CART_STORAGE_KEY,
  type CartAction,
  type CartState,
} from '@bonboca/shared-domain';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';

export { CART_TTL_MILLISECONDS } from '@bonboca/shared-domain';
export type { CartItem, CartState, CartAction } from '@bonboca/shared-domain';

interface CartContextValue {
  state: CartState | null;
  itemCount: number;
  subtotalMinorUnits: bigint;
  addItem: (input: Extract<CartAction, { type: 'ADD_ITEM' }>) => void;
  setQuantity: (menuItemId: string, quantity: number) => void;
  removeItem: (menuItemId: string) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

function readPersistedCart(now: number): CartState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = cartStateSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) {
      window.localStorage.removeItem(CART_STORAGE_KEY);
      return null;
    }
    if (isCartExpired(parsed.data, now)) {
      window.localStorage.removeItem(CART_STORAGE_KEY);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writePersistedCart(state: CartState | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (state === null) {
      window.localStorage.removeItem(CART_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export { cartReducer };

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(
    (current: CartState | null, action: CartAction) => cartReducer(current, action),
    null,
  );
  const hydrated = useRef(false);

  useEffect(() => {
    dispatch({ type: 'HYDRATE', state: readPersistedCart(Date.now()) });
    hydrated.current = true;
  }, []);

  useEffect(() => {
    if (!hydrated.current) return;
    writePersistedCart(state);
  }, [state]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const fresh = readPersistedCart(Date.now());
      dispatch({ type: 'HYDRATE', state: fresh });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const addItem = useCallback((action: Extract<CartAction, { type: 'ADD_ITEM' }>) => {
    dispatch(action);
  }, []);
  const setQuantity = useCallback((menuItemId: string, quantity: number) => {
    dispatch({ type: 'SET_QUANTITY', menuItemId, quantity });
  }, []);
  const removeItem = useCallback((menuItemId: string) => {
    dispatch({ type: 'REMOVE_ITEM', menuItemId });
  }, []);
  const clear = useCallback(() => {
    dispatch({ type: 'CLEAR' });
  }, []);

  const itemCount = useMemo(
    () => (state?.items ?? []).reduce((acc, entry) => acc + entry.quantity, 0),
    [state],
  );

  const subtotalMinorUnits = useMemo(() => {
    if (!state) return 0n;
    return computeCartPricing({
      items: state.items.map((e) => ({ ...e, unitPriceMinorUnits: BigInt(e.unitPriceMinorUnits) })),
      acceptsPickup: state.acceptsPickup,
      deliveryTiers: [],
    }).subtotalMinorUnits;
  }, [state]);

  const value: CartContextValue = useMemo(
    () => ({ state, itemCount, subtotalMinorUnits, addItem, setQuantity, removeItem, clear }),
    [state, itemCount, subtotalMinorUnits, addItem, setQuantity, removeItem, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
